import type { ProductFormData, MediaItem, Draft } from '@/types';

const API_BASE = import.meta.env.VITE_API_URL || 'https://sggadgets-backend.aadhithyanannamalai.workers.dev';

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    if (res.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `API error: ${res.status}`);
  }

  return res.json();
}

export const api = {
  products: {
    list: (params?: { page?: number; limit?: number; search?: string }) => {
      const qs = new URLSearchParams();
      if (params?.page) qs.set('page', String(params.page));
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.search) qs.set('search', params.search);
      return request<{ products: ProductFormData[]; total: number; page: number }>(
        `/products?${qs}`,
      );
    },
    get: (id: string) => request<ProductFormData>(`/products/${id}`),
    create: (data: ProductFormData) =>
      request<ProductFormData>('/products', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: Partial<ProductFormData>) =>
      request<ProductFormData>(`/products/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/products/${id}`, { method: 'DELETE' }),
    publish: (id: string) =>
      request<ProductFormData>(`/products/${id}/publish`, { method: 'POST' }),
    archive: (id: string) =>
      request<ProductFormData>(`/products/${id}/archive`, { method: 'POST' }),
  },

  media: {
    list: (params?: { type?: 'image' | 'video'; page?: number; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.type) qs.set('type', params.type);
      if (params?.page) qs.set('page', String(params.page));
      if (params?.limit) qs.set('limit', String(params.limit));
      return request<{ items: MediaItem[]; total: number }>(`/media?${qs}`);
    },
    getPresignedUrl: (filename: string, mimeType: string) =>
      request<{ uploadUrl: string; key: string; publicUrl: string }>(
        '/media/presign',
        {
          method: 'POST',
          body: JSON.stringify({ filename, mimeType }),
        },
      ),
    delete: (key: string) =>
      request<{ success: boolean }>(`/media/${encodeURIComponent(key)}`, {
        method: 'DELETE',
      }),
  },

  drafts: {
    list: () => request<Draft[]>('/drafts'),
    save: (data: Partial<ProductFormData>) =>
      request<Draft>('/drafts', {
        method: 'POST',
        body: JSON.stringify({ data }),
      }),
    update: (id: string, data: Partial<ProductFormData>) =>
      request<Draft>(`/drafts/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ data }),
      }),
    delete: (id: string) =>
      request<{ success: boolean }>(`/drafts/${id}`, { method: 'DELETE' }),
  },

  batch: {
    submit: (rows: Record<string, string>[]) =>
      request<{ jobId: string; total: number }>('/batch/import', {
        method: 'POST',
        body: JSON.stringify({ rows }),
      }),
    status: (jobId: string) =>
      request<{
        jobId: string;
        status: 'pending' | 'processing' | 'completed' | 'failed';
        progress: number;
        successCount: number;
        errorCount: number;
        errors: { row: number; message: string }[];
      }>(`/batch/${jobId}`),
  },

  ai: {
    generateDescription: (productName: string, category: string) =>
      request<{ description: string }>('/ai/description', {
        method: 'POST',
        body: JSON.stringify({ productName, category }),
      }),
    generateSpecs: (productName: string, category: string) =>
      request<{ specs: Record<string, string> }>('/ai/specs', {
        method: 'POST',
        body: JSON.stringify({ productName, category }),
      }),
    suggestPricing: (cost: number, category: string) =>
      request<{ suggestedPrice: number; margin: number }>(
        '/ai/pricing',
        {
          method: 'POST',
          body: JSON.stringify({ cost, category }),
        },
      ),
  },

  settings: {
    get: () => request<Record<string, string>>('/settings'),
    update: (data: Record<string, string>) =>
      request<Record<string, string>>('/settings', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },
};
